import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  FileText,
  Phone,
  ShieldAlert,
  User,
  Upload,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { useAuth } from "../lib/auth";
import { CITIES, api, formatApiError } from "../lib/gj";

const BUSINESS_TYPES = ["Dealer", "Broker", "Individual"];

const INDIAN_STATES = [
  "West Bengal",
  "Bihar",
  "Jharkhand",
  "Odisha",
  "Assam",
  "Sikkim",
  "Delhi",
  "Maharashtra",
  "Karnataka",
  "Tamil Nadu",
  "Uttar Pradesh",
  "Madhya Pradesh",
  "Rajasthan",
  "Gujarat",
  "Punjab",
  "Haryana",
  "Telangana",
  "Andhra Pradesh",
  "Kerala",
  "Other",
];

const MAX_DOC_BYTES = 2 * 1024 * 1024; // 2 MB client-side guard

const DOC_FIELDS = [
  { key: "trade_license", label: "Trade License" },
  { key: "gst", label: "GST Certificate" },
  { key: "id_proof", label: "ID Proof (Aadhaar / PAN / Voter ID)" },
  { key: "address_proof", label: "Address Proof" },
];

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-[#F97316]" />
      <h2 className="font-heading text-lg font-bold text-[#0A192F]">{title}</h2>
    </div>
    {subtitle && (
      <p className="text-xs text-[#718096] mt-1">{subtitle}</p>
    )}
    <div className="gj-divider-gold mt-3" />
  </div>
);

const Field = ({ label, required, htmlFor, children, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-[#0A192F]">
      {label} {required && <span className="text-[#9B2C2C]">*</span>}
    </Label>
    {children}
    {hint && <p className="text-[11px] text-[#718096]">{hint}</p>}
  </div>
);

export default function DealerOnboarding() {
  const nav = useNavigate();
  const { refresh: refreshAuth } = useAuth();

  const [form, setForm] = useState({
    business_name: "",
    owner_name: "",
    business_type: "Dealer",
    stock_count: "",
    mobile: "",
    whatsapp: "",
    same_whatsapp: true,
    email: "",
    password: "",
    business_address: "",
    city: "Kolkata",
    state: "West Bengal",
  });

  const [docs, setDocs] = useState({
    trade_license: null,
    gst: null,
    id_proof: null,
    address_proof: null,
  });

  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onWhatsappToggle = (v) => {
    setForm((f) => ({
      ...f,
      same_whatsapp: !!v,
      whatsapp: v ? f.mobile : f.whatsapp,
    }));
  };

  const onMobileChange = (val) => {
    setForm((f) => ({
      ...f,
      mobile: val,
      whatsapp: f.same_whatsapp ? val : f.whatsapp,
    }));
  };

  const onDocChange = (key, file) => {
    if (!file) {
      setDocs((d) => ({ ...d, [key]: null }));
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error(`${file.name} is over 2 MB. Please upload a smaller file.`);
      return;
    }
    setDocs((d) => ({ ...d, [key]: file }));
  };

  const isValid = useMemo(() => {
    return (
      form.business_name.trim().length >= 2 &&
      form.owner_name.trim().length >= 2 &&
      /^\d{10}$/.test(form.mobile.trim()) &&
      /^\d{10}$/.test(form.whatsapp.trim()) &&
      /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
      form.password.length >= 6 &&
      form.business_address.trim().length >= 5 &&
      form.city.trim().length > 0 &&
      form.state.trim().length > 0 &&
      consent
    );
  }, [form, consent]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please accept the declaration to continue.");
      return;
    }
    if (!isValid) {
      toast.error("Please fill all required fields correctly.");
      return;
    }
    setSubmitting(true);

    // Phase 2: full multipart submission to the dedicated onboarding endpoint.
    const fd = new FormData();
    fd.append("business_name", form.business_name.trim());
    fd.append("owner_name", form.owner_name.trim());
    fd.append("business_type", form.business_type);
    if (form.stock_count) fd.append("stock_count", form.stock_count);
    fd.append("mobile", form.mobile.trim());
    fd.append("whatsapp", form.whatsapp.trim());
    fd.append("email", form.email.trim().toLowerCase());
    fd.append("password", form.password);
    fd.append("business_address", form.business_address.trim());
    fd.append("city", form.city.trim());
    fd.append("state", form.state.trim());
    fd.append("consent_accepted", "true");
    for (const k of ["trade_license", "gst", "id_proof", "address_proof"]) {
      if (docs[k]) fd.append(k, docs[k]);
    }

    try {
      const { data } = await api.post("/auth/onboard-dealer", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshAuth();
      toast.success(
        `Welcome aboard, ${data.user?.name || "dealer"}! Our team will verify your details shortly.`
      );
      nav("/dealer", { replace: true });
    } catch (err) {
      console.error("[onboarding] submit failed:", err?.response?.data || err);
      toast.error(
        formatApiError(err?.response?.data?.detail) ||
          (err?.response?.status ? `Server error (${err.response.status}). Please try again.` : null) ||
          err?.message ||
          "Could not complete onboarding. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F9F8F6]" data-testid="dealer-onboarding-page">
      {/* Hero strip */}
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
          <Link to="/" className="text-xs text-white/60 hover:text-[#F97316] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
          <div className="gj-divider-gold mt-4 mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Become a Verified <span className="text-[#F97316]">Gaddi Junction</span> Dealer
          </h1>
          <p className="mt-2 text-white/70 max-w-2xl text-sm md:text-base">
            List your inventory free for 30 days. Get WhatsApp leads from genuine buyers across West
            Bengal. Quick registration — admin verifies within 24 hours.
          </p>
        </div>
      </div>

      <form
        onSubmit={onSubmit}
        className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-14"
        data-testid="dealer-onboarding-form"
        noValidate
      >
        <div className="grid lg:grid-cols-[1fr_320px] gap-8">
          {/* LEFT: form */}
          <div className="space-y-8">
            {/* Business */}
            <section className="bg-white border border-[#0A192F]/10 rounded-xl p-6 md:p-7 gj-shadow-card">
              <SectionTitle
                icon={Briefcase}
                title="Business Details"
                subtitle="Tell us about your dealership"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Dealer / Showroom Name" required htmlFor="business_name">
                  <Input
                    id="business_name"
                    value={form.business_name}
                    onChange={(e) => set("business_name", e.target.value)}
                    placeholder="Kolkata Auto Hub"
                    data-testid="ob-business-name"
                  />
                </Field>
                <Field label="Owner Name" required htmlFor="owner_name">
                  <Input
                    id="owner_name"
                    value={form.owner_name}
                    onChange={(e) => set("owner_name", e.target.value)}
                    placeholder="Rajesh Kumar"
                    data-testid="ob-owner-name"
                  />
                </Field>
                <Field label="Business Type" required>
                  <Select
                    value={form.business_type}
                    onValueChange={(v) => set("business_type", v)}
                  >
                    <SelectTrigger data-testid="ob-business-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Cars in Stock" htmlFor="stock_count" hint="Approximate — you can update later">
                  <Input
                    id="stock_count"
                    type="number"
                    min={0}
                    value={form.stock_count}
                    onChange={(e) => set("stock_count", e.target.value)}
                    placeholder="e.g. 12"
                    data-testid="ob-stock-count"
                  />
                </Field>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-white border border-[#0A192F]/10 rounded-xl p-6 md:p-7 gj-shadow-card">
              <SectionTitle
                icon={Phone}
                title="Contact Information"
                subtitle="How buyers and our team will reach you"
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Mobile Number" required htmlFor="mobile" hint="10-digit Indian mobile, without +91">
                  <Input
                    id="mobile"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.mobile}
                    onChange={(e) => onMobileChange(e.target.value.replace(/\D/g, ""))}
                    placeholder="98XXXXXXXX"
                    data-testid="ob-mobile"
                  />
                </Field>
                <Field label="WhatsApp Number" required htmlFor="whatsapp">
                  <Input
                    id="whatsapp"
                    inputMode="numeric"
                    maxLength={10}
                    value={form.whatsapp}
                    onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, ""))}
                    placeholder="98XXXXXXXX"
                    disabled={form.same_whatsapp}
                    data-testid="ob-whatsapp"
                  />
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <Checkbox
                      checked={form.same_whatsapp}
                      onCheckedChange={onWhatsappToggle}
                      data-testid="ob-same-whatsapp"
                    />
                    <span className="text-xs text-[#4A5568]">Same as mobile</span>
                  </label>
                </Field>
                <Field label="Email Address" required htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@dealership.com"
                    data-testid="ob-email"
                  />
                </Field>
                <Field label="Create Password" required htmlFor="password" hint="Minimum 6 characters">
                  <Input
                    id="password"
                    type="password"
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                    placeholder="••••••••"
                    data-testid="ob-password"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Business Address" required htmlFor="business_address">
                    <Textarea
                      id="business_address"
                      rows={3}
                      value={form.business_address}
                      onChange={(e) => set("business_address", e.target.value)}
                      placeholder="Shop / showroom address with landmark"
                      data-testid="ob-address"
                    />
                  </Field>
                </div>
                <Field label="City" required>
                  <Select value={form.city} onValueChange={(v) => set("city", v)}>
                    <SelectTrigger data-testid="ob-city">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CITIES.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="State" required>
                  <Select value={form.state} onValueChange={(v) => set("state", v)}>
                    <SelectTrigger data-testid="ob-state">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </section>

            {/* Documents */}
            <section className="bg-white border border-[#0A192F]/10 rounded-xl p-6 md:p-7 gj-shadow-card">
              <SectionTitle
                icon={Upload}
                title="Business Documents"
                subtitle="Optional — speeds up admin verification. Max 2 MB each. PDF / JPG / PNG."
              />
              <div className="grid sm:grid-cols-2 gap-4">
                {DOC_FIELDS.map((d) => {
                  const f = docs[d.key];
                  return (
                    <Field key={d.key} label={d.label} htmlFor={`doc-${d.key}`}>
                      <Input
                        id={`doc-${d.key}`}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => onDocChange(d.key, e.target.files?.[0] || null)}
                        data-testid={`ob-doc-${d.key}`}
                      />
                      {f && (
                        <p className="text-[11px] text-[#0A192F] mt-1 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-[#F97316]" /> {f.name}
                        </p>
                      )}
                    </Field>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#718096] mt-4">
                Uploaded files are stored securely. Optional but speeds up verification.
              </p>
            </section>

            {/* Disclaimer + Consent */}
            <section
              className="bg-white border-2 border-[#F97316]/40 rounded-xl p-6 md:p-7"
              data-testid="ob-consent-section"
            >
              <SectionTitle
                icon={ShieldAlert}
                title="Important Disclaimer"
                subtitle="Please read carefully before submitting"
              />
              <div className="bg-[#FFF7ED] border border-[#F97316]/30 rounded-lg p-4 text-sm text-[#0A192F] space-y-2">
                <p className="font-semibold">
                  Gaddi Junction is only a digital marketplace platform. We are NOT responsible for:
                </p>
                <ul className="list-disc pl-5 space-y-1 text-[#4A5568]">
                  <li>Vehicle condition, mechanical health, or accident history</li>
                  <li>Payment disputes between buyer and seller</li>
                  <li>RC transfer disputes or registration-related issues</li>
                  <li>Fraud by buyers, sellers, or any third party</li>
                </ul>
                <p className="text-xs text-[#4A5568] pt-1">
                  All transactions happen directly between dealer and buyer. Verify everything
                  in person before payment.
                </p>
              </div>

              <label
                className="mt-5 flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-[#F3F0EA]/40"
                data-testid="ob-consent-label"
              >
                <Checkbox
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-1"
                  data-testid="ob-consent-checkbox"
                />
                <span className="text-sm text-[#0A192F] leading-relaxed">
                  I confirm that <strong>all vehicle details I provide are genuine</strong>, that
                  I am <strong>authorized to sell / list the vehicles</strong> I post,
                  that I accept the{" "}
                  <Link to="/terms" target="_blank" className="text-[#0A192F] underline font-semibold hover:text-[#F97316]">
                    Terms &amp; Conditions
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" className="text-[#0A192F] underline font-semibold hover:text-[#F97316]">
                    Privacy Policy
                  </Link>
                  , and that I understand Gaddi Junction is only a digital marketplace platform
                  and not a party to any sale.
                </span>
              </label>
            </section>

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              <Button
                type="submit"
                disabled={submitting || !isValid}
                className="bg-[#F97316] hover:bg-[#EA580C] disabled:bg-[#F97316]/40 disabled:cursor-not-allowed text-[#0A192F] font-bold rounded-lg px-8 py-6"
                data-testid="ob-submit-btn"
              >
                {submitting ? "Submitting…" : "Complete Registration"}
              </Button>
              <p className="text-xs text-[#718096]">
                Already onboarded?{" "}
                <Link to="/login" className="text-[#0A192F] font-semibold hover:text-[#F97316]">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* RIGHT: sticky help / legal sidebar */}
          <aside className="space-y-4">
            <div className="bg-white border border-[#0A192F]/10 rounded-xl p-5 lg:sticky lg:top-24">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#F97316]" />
                <span className="font-heading font-bold text-[#0A192F]">Why join us</span>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-[#4A5568]">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#F97316] mt-0.5" /> Free for 30 days</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#F97316] mt-0.5" /> WhatsApp leads directly</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#F97316] mt-0.5" /> Verified Dealer badge</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#F97316] mt-0.5" /> Zero commission</li>
              </ul>
            </div>

            <div className="bg-white border border-[#0A192F]/10 rounded-xl p-5">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#F97316]" />
                <span className="font-heading font-bold text-[#0A192F]">Legal</span>
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/terms" className="text-[#4A5568] hover:text-[#0A192F]">Terms &amp; Conditions</Link></li>
                <li><Link to="/privacy" className="text-[#4A5568] hover:text-[#0A192F]">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="text-[#4A5568] hover:text-[#0A192F]">Refund &amp; Cancellation</Link></li>
                <li><Link to="/disclaimer" className="text-[#4A5568] hover:text-[#0A192F]">Disclaimer</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </form>
    </div>
  );
}
